import { Calendar, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";

const Blog = () => {
  const { t } = useLanguage();
  // Sample blog posts - in production, these would come from a CMS or database
  const posts = [
    {
      title: "Understanding the Asylum Process in 2025",
      excerpt: "A comprehensive guide to seeking asylum in the United States, including recent changes and what to expect.",
      date: "2025-01-15",
      category: "Immigration",
      slug: "understanding-asylum-process-2025",
    },
    {
      title: "What to Do If You're Facing Deportation",
      excerpt: "Critical steps to take immediately if you receive a Notice to Appear or are facing removal proceedings.",
      date: "2025-01-10",
      category: "Immigration",
      slug: "what-to-do-facing-deportation",
    },
    {
      title: "Citizenship Through Naturalization: Complete Guide",
      excerpt: "Everything you need to know about becoming a U.S. citizen through naturalization, from eligibility to the ceremony.",
      date: "2025-01-05",
      category: "Immigration",
      slug: "citizenship-naturalization-guide",
    },
    {
      title: "Your Rights During a Police Stop",
      excerpt: "Know your constitutional rights during traffic stops and police encounters to protect yourself.",
      date: "2024-12-20",
      category: "Criminal Defense",
      slug: "rights-during-police-stop",
    },
    {
      title: "How to Handle a Personal Injury Claim",
      excerpt: "Step-by-step guide to maximizing your personal injury settlement after an accident.",
      date: "2024-12-15",
      category: "Personal Injury",
      slug: "handle-personal-injury-claim",
    },
    {
      title: "Divorce in Texas: What You Need to Know",
      excerpt: "Understanding Texas divorce laws, property division, and child custody considerations.",
      date: "2024-12-10",
      category: "Family Law",
      slug: "divorce-texas-guide",
    },
  ];

  const categories = ["All", "Immigration", "Criminal Defense", "Personal Injury", "Family Law", "Business Law"];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Legal Blog & Resources | Daniel Albert Law Firm"
        description="Expert legal insights on immigration, criminal defense, family law, and more. Stay informed with our comprehensive legal guides and articles."
        keywords="legal blog, immigration news, criminal defense tips, family law advice, Houston attorney blog"
      />
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('blog.title')}</h1>
            <p className="text-xl opacity-95">
              {t('blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          {/* Categories Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={category === "All" ? "default" : "outline"}
                size="sm"
              >
                {category === "All" ? t('blog.all') : category}
              </Button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow flex flex-col">
                <CardHeader>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                  </div>
                  <div className="inline-block mb-2">
                    <span className="text-xs font-semibold bg-accent/10 text-accent px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <CardTitle className="text-xl">{post.title}</CardTitle>
                  <CardDescription>{post.excerpt}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button asChild variant="link" className="p-0">
                    <a href={`/blog/${post.slug}`}>
                      {t('blog.read')} <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Newsletter Signup */}
          <div className="mt-16 max-w-2xl mx-auto bg-gradient-subtle p-8 rounded-lg shadow-md text-center">
            <h2 className="text-2xl font-bold mb-4">{t('blog.subscribe')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('blog.subscribe.desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t('blog.email')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <Button variant="cta" className="whitespace-nowrap">
                {t('blog.subscribe.btn')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Have Legal Questions?"
        description="Our experienced attorneys are here to provide personalized guidance for your specific situation."
      />

      <Footer />
    </div>
  );
};

export default Blog;
